import { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export const EXPLAINER_VIDEO_URL =
  'https://www.dropbox.com/scl/fi/4swzd9k601uvhumq0xk1d/CareTailor-CarersV2.mov?rlkey=tm2e9g85aa8agc7rn3llq98q3&raw=1';

type ExplainerVideoModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ExplainerVideoModal: FC<ExplainerVideoModalProps> = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) setIsLoading(true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="CareTailor explainer video"
    >
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close video"
          className="absolute -top-10 right-0 text-white text-3xl leading-none hover:opacity-70"
        >
          &times;
        </button>
        <div className="relative w-full aspect-video max-h-[80vh] rounded-xl bg-black overflow-hidden shadow-2xl">
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white">
              <span className="loading loading-spinner loading-lg" />
              <span className="text-sm opacity-80">Loading video…</span>
            </div>
          )}
          <video
            src={EXPLAINER_VIDEO_URL}
            controls
            autoPlay
            playsInline
            preload="auto"
            onCanPlay={() => setIsLoading(false)}
            onPlaying={() => setIsLoading(false)}
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ${
              isLoading ? 'opacity-0' : 'opacity-100'
            }`}
          />
        </div>
      </div>
    </div>,
    document.getElementById('root') ?? document.body
  );
};

export default ExplainerVideoModal;
